import { FormImageInput } from "components/Extra/FormImageInput"
import { FormInput } from "components/Extra/FormInput"
import FormLabel from "components/Extra/FormLabel"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Col,
  FormFeedback,
  FormGroup,
  Row,
} from "reactstrap"
import Select from "react-select"
import { offerTypes } from "pages/vendor/Offers/Modals/AddNewOffer/AddNewOffer"
import { productOptions } from "pages/vendor/Products"
import { CurrentStage } from "pages/vendor/Offers/Modals/AddNewOffer/AddNewOffer"
import { useTranslation } from "react-i18next"


interface BasicInfoSectionProps {
  openFirstAccordion: string
  setOpenFirstAccordion: React.Dispatch<React.SetStateAction<string>>
  validation: any
  selectedofferType: {
    id: string
    name: string
  } | null
  setselectedofferType: React.Dispatch<
    React.SetStateAction<{ id: string; name: string } | null>
  >
  currentStage: CurrentStage
}

export const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  openFirstAccordion,
  setOpenFirstAccordion,
  validation,
  selectedofferType,
  setselectedofferType,
  currentStage,
}) => {
  const { t } = useTranslation()

  const selectProductRef = useRef<any>(null)
  const [selectedProducts, setselectedProducts] = useState<
    | {
        label: string
        value: string
      }[]
    | []
  >([])

  // Group products by category following the FormAdvanced.js pattern
  const groupedProductOptions = useMemo(() => {
    const categoryMap = new Map<string, typeof productOptions>()

    // Group products by category
    productOptions.forEach(product => {
      if (!categoryMap.has(product.category)) {
        categoryMap.set(product.category, [])
      }
      categoryMap.get(product.category)?.push(product)
    })

    // Convert to react-select grouped format
    const options = Array.from(categoryMap.entries())
      .map(([categoryName, products]) => ({
        label: categoryName,
        options: products.map(product => ({
          label: product.name,
          value: product.id
        }))
      }))
      .sort((a, b) => a.label.localeCompare(b.label))

    return options
  }, [])

  const handleRemove = useCallback(() => {
    validation.setFieldValue("offerImage", null)
  }, [])

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return

    const file = e.target.files[0]
    const singleImage = {
      id: `${Date.now()}`,
      name: file.name,
      file,
      preview: URL.createObjectURL(file),
    }

    validation.setFieldValue("offerImage", singleImage)
  }

  const selectProductHandler = useCallback(
    (selectedOptions: any) => {
      const productData = selectedOptions?.map((option: any) => ({
        id: option.value,
        name: option.label
      })) || []

      setselectedProducts(selectedOptions || [])
      validation.setFieldValue("products", productData)
    },
    [validation]
  )

  // State for selected categories
  const [selectedCategories, setSelectedCategories] = useState<any[]>([])

  // State for toggle between category and product selection
  const [isProductSelectionEnabled, setIsProductSelectionEnabled] = useState(true)

  // Category selection handler - selects all products in chosen categories
  const selectCategoryHandler = useCallback((selectedCategoryOptions: any) => {
    if (!selectedCategoryOptions || selectedCategoryOptions.length === 0) {
      setSelectedCategories([])
      setselectedProducts([])
      validation.setFieldValue("products", [])
      return
    }

    // Get all products from selected categories
    let allProducts: any[] = []
    selectedCategoryOptions.forEach((categoryOption: any) => {
      const category = groupedProductOptions.find(g => g.label === categoryOption.value)
      if (category && category.options) {
        allProducts = [...allProducts, ...category.options]
      }
    })

    setSelectedCategories(selectedCategoryOptions)
    setselectedProducts(allProducts)

    const productData = allProducts.map((option: any) => ({
      id: option.value,
      name: option.label
    }))
    validation.setFieldValue("products", productData)
  }, [groupedProductOptions, validation])

  // Sync categories when products are manually selected
  useEffect(() => {
    if (!selectedProducts || selectedProducts.length === 0) {
      setSelectedCategories([])
      return
    }

    // Find which categories have all their products selected
    const fullySelectedCategories: any[] = []
    groupedProductOptions.forEach((category: any) => {
      if (!category.options) return

      const categoryProductIds = category.options.map((opt: any) => opt.value)
      const selectedProductIds = selectedProducts.map((prod: any) => prod.value)

      // Check if all products in this category are selected
      const allSelected = categoryProductIds.every((id: any) => selectedProductIds.includes(id))
      if (allSelected) {
        fullySelectedCategories.push({
          value: category.label,
          label: category.label
        })
      }
    })

    setSelectedCategories(fullySelectedCategories)
  }, [selectedProducts, groupedProductOptions])

  // Create options for category select
  const categorySelectOptions = useMemo(() => {
    return groupedProductOptions.map((category: any) => ({
      value: category.label,
      label: `${category.label} (${category.options?.length || 0} products)`
    }))
  }, [groupedProductOptions])

  const addNewProductHandler = useCallback(() => {
    if (!selectProductRef.current) return
    selectProductRef.current.focus?.()
    if (typeof selectProductRef.current.openMenu === "function") {
      selectProductRef.current.openMenu()
    } else {
      const control = selectProductRef.current.controlRef ?? null
      if (control)
        control.dispatchEvent(
          new MouseEvent("mousedown", {
            bubbles: true,
            cancelable: true,
            view: window,
          })
        )
    }
  }, [])
  
  return (
    <Accordion open={openFirstAccordion}>
      <AccordionItem>
        <AccordionHeader
          targetId="0"
          onClick={() =>
            setOpenFirstAccordion(openFirstAccordion === "0" ? "1" : "0")
          }
        >
          {t("common_basicInfo")}
        </AccordionHeader>
        <AccordionBody accordionId="0">
          <FormInput
            label={t("offers_offerTitle") as string}
            name="offerTitle"
            type="text"
            placeholder={t("offers_enterOfferTitle") as string}
            validation={validation}
            required
            readOnly={currentStage === "preview"}
          />
          <FormGroup>
            <FormLabel htmlFor="offerType" required>
              {t("offers_offerType")}
            </FormLabel>
            <Select
              id="offerType"
              value={selectedofferType}
              onChange={(selectedOption: { id: string; name: string }) => {
                if (currentStage === "preview") return
                setselectedofferType(selectedOption)
                validation.setFieldValue("offerType", {
                  id: selectedOption?.id,
                  name: selectedOption?.name,
                })
              }}
              options={offerTypes}
              getOptionLabel={(option: { id: string; name: string }) =>
                option.name
              }
              getOptionValue={(option: { id: string; name: string }) =>
                option.id
              }
              className="select2-selection"
            />
            {validation.touched.offerType && validation.errors.offerType ? (
              <FormFeedback type="invalid">
                {validation.errors.offerType}
              </FormFeedback>
            ) : null}
          </FormGroup>
          {currentStage === "addNewOffer" && (
            <>
              <FormGroup>
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <FormLabel className="mb-0 me-3">
                      {isProductSelectionEnabled
                        ? t("offers_products")
                        : t("offers_selectByCategory")
                      }
                    </FormLabel>
                    <div
                      className="form-check form-switch"
                      style={{ cursor: "pointer" }}
                      onClick={() => setIsProductSelectionEnabled(!isProductSelectionEnabled)}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={isProductSelectionEnabled}
                        onChange={() => setIsProductSelectionEnabled(!isProductSelectionEnabled)}
                      />
                    </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-start">
                    <div className="flex-grow-1 me-2">
                  {!isProductSelectionEnabled ? (
                    <Select
                      id="categories"
                      value={selectedCategories}
                      isMulti
                      isClearable
                      onChange={selectCategoryHandler}
                      options={categorySelectOptions}
                      className="select2-selection"
                      classNamePrefix="select2"
                      placeholder={t("offers_selectByCategory")}
                    />
                  ) : (
                        <Select
                          ref={selectProductRef}
                          id="products"
                          value={selectedProducts}
                          isMulti
                          isClearable
                          onChange={selectProductHandler}
                          options={groupedProductOptions}
                          className="select2-selection"
                          classNamePrefix="select2"
                          placeholder={t("offers_products")}
                        />
                      )}
                    </div>
                    <span
                      className="input-group-append"
                      style={{ cursor: "pointer" }}
                      onClick={addNewProductHandler}
                    >
                      <i className="bx bx-plus font-size-18 p-2 px-2 input-group-text"></i>
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-1 mt-2">
                    <i className="bx bx-info-circle"></i>
                    <FormFeedback
                      type="invalid"
                      className="d-block text-secondary"
                    >
                      {t("offers_multipleProduct")}
                    </FormFeedback>
                  </div>
                </div>
              </FormGroup>
              <FormImageInput
                label={t("offers_offerImage") as string}
                changeImageHandler={handleFilesChange}
                name="offerImage"
                validation={validation}
                removeHandler={handleRemove}
                info={t("offers_offerImageInfo") as string}
              />
            </>
          )}
        </AccordionBody>
      </AccordionItem>
    </Accordion>
  )
}
